import React from 'react';
import { Tab } from 'semantic-ui-react';
import intl from 'react-intl-universal';
import { Link } from 'react-router-dom';
import _compact from 'lodash/compact';
import {
  formatUnixDateForDisplay,
  formatUnixTimeOfDayForDisplay,
  formatMinutesForDisplay,
  secondsToMilliseconds
} from 'utils/date-time-utils';
import * as styles from './PastMeetingDetails.scss';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import ParticipantsTab from './ParticipantsTab/ParticipantsTab';
import {
  PAST_MEETING_PARTICIPANTS,
  PAST_MEETING_TAB,
  PAST_MEETING_GEO
} from 'constants/pastMeetings';
import Timer = NodeJS.Timer;
import { PAST_MEETING_REFRESH_TIME } from 'constants/pastMeetings';
import ListViewTabLayout from 'components/ListViewTabLayout/ListViewTabLayout';
import { GeoDistribution } from 'components/GeoDistribution/GeoDistribution';
import {
  DWHPastConferenceModeratorType,
  DWHPastConferenceRecordingType,
  GeoLocation,
  ParticipantEntry
} from 'interfaces/Dashboard';
import { PastMeetingDetailsData } from 'interfaces/PastMeeting';
import { GraphqlQueryControls } from 'react-apollo';

interface Props {
  loading: boolean;
  data: GraphqlQueryControls<PastMeetingDetailsData>;
  pastConferenceDetails: boolean;
  meetingName: string;
  extension: string;
  conferenceType: string;
  moderators: Array<DWHPastConferenceModeratorType>;
  recordings: Array<DWHPastConferenceRecordingType>;
  quality: string;
  startedAt: number;
  totalParticipants: number;
  endedAt: number;
  duration: number;
  participants: Array<ParticipantEntry>;
  conferenceId: string;
  geoDistribution: Array<GeoLocation>;
  showGeoDistribution: boolean;
}

const indexKeys = {
  [PAST_MEETING_PARTICIPANTS]: 0,
  [PAST_MEETING_GEO]: 1,
};

let refetchInterval: Timer | null;

const panes = (
  participants: Array<ParticipantEntry>,
  conferenceId: string,
  geoDistribution: Array<GeoLocation>,
  showGeoDistribution: boolean,
  meetingName: string,
) => {
  const paneList =
    [
      {
        menuItem: (
          {
            key: 'participantsTab',
            content: (
              intl.get('participants')
            )
          }),
        render: () => (
          <Tab.Pane key={'0'} attached={false}>
            <ParticipantsTab
              data={participants}
              conferenceId={conferenceId}
              meetingName={meetingName}
            />
          </Tab.Pane>
        )
      }
    ];

  if (showGeoDistribution) { paneList.push({
    menuItem: (
      {
        key: 'geoTab',
        content: (
          intl.get('geoDistribution')
        )
      }),
    render: () => (
      <Tab.Pane key={'1'} attached={false}>
        <GeoDistribution geoDistribution={geoDistribution}/>
      </Tab.Pane>
    )
  });
  }

  return _compact(paneList);
};

const PastMeetingDetails = (props: Props) => {
  const {
    loading,
    data,
    pastConferenceDetails,
    meetingName,
    extension,
    conferenceType,
    moderators,
    recordings,
    quality,
    startedAt,
    totalParticipants,
    endedAt,
    duration,
    participants,
    conferenceId,
    geoDistribution,
    showGeoDistribution,
  } = props;
  const { refetch } = data;

  if (loading) {
    return <LoadingIndicator/>;
  }

  if (!loading && !pastConferenceDetails) {
    if (!refetchInterval && refetch) {
      refetchInterval = setInterval(refetch, PAST_MEETING_REFRESH_TIME);
    }
    return <LoadingIndicator/>;
  }

  if (refetchInterval) {
    clearInterval(refetchInterval);
    refetchInterval = null;
  }

  const moderatorList = moderators
    .reduce(
      (acc: Array<string>, cur: DWHPastConferenceModeratorType) => {
        if (cur.displayName) {
          acc.push(cur.displayName);
        }
        return acc;
      },
      []
    );

  const recordingsList = recordings
    .reduce(
      (acc: Array<JSX.Element>, cur: DWHPastConferenceRecordingType) => {
        if (cur.recordingId) {
          acc.push(
            <Link to={{ pathname: `/singleRecording/${cur.recordingId}` }}>
              {intl.get('clickToView')}
            </Link>
          );
        }
        return acc;
      },
      []
    );

  return (
    <div className={styles.container}>
      <ListViewTabLayout
        title={meetingName}
        breadcrumbs={(
          <div>
            <Link key="pastMeetingsBreadcrumb" to={{pathname: PAST_MEETING_TAB}}>
              {intl.get('pastMeetings')}
            </Link>
            {' > '}
            <span>
              {meetingName}
            </span>
          </div>
        )}
        subtitle={(
          <div>
            <div className={styles.flexCol}>
              <ul className={styles.headerList}>
                <li>{intl.get('moderatorWithColon')} {moderatorList.join(', ')}</li>
                <li>{intl.get('extensionWithColon')} {extension}</li>
                <li>{intl.get('meetingTypeWithColon')} {conferenceType}</li>
                {quality && <li>{intl.get('qualityWithColon')} {intl.get('minimal')}</li>}
                <li>{intl.get('recordingWithColon')} {recordingsList.length ? recordingsList[0] : intl.get('none')}</li>
              </ul>
              <ul className={styles.headerList}>
                <li>{intl.get('dateWithColon')} {formatUnixDateForDisplay(secondsToMilliseconds(startedAt))}</li>
                <li>{intl.get('totalParticipantsWithColon')} {totalParticipants}</li>
                <li>{intl.get('startTimeWithColon')} {formatUnixTimeOfDayForDisplay(secondsToMilliseconds(startedAt))}</li>
                <li>{intl.get('endTimeWithColon')} {formatUnixTimeOfDayForDisplay(secondsToMilliseconds(endedAt))}</li>
                <li>{intl.get('durationWithColon')} {formatMinutesForDisplay(duration)}</li>
              </ul>
            </div>
          </div>
        )}
        key={'header'}
        panes={panes(participants, conferenceId, geoDistribution, showGeoDistribution, meetingName)}
        indexKeys={indexKeys}
      />
    </div>
  );
};

export default PastMeetingDetails;
